import React, { MouseEvent, FC, useCallback, useEffect, useState, ChangeEvent } from 'react'
import {
  Box,
  CircularProgress,
  IconButton,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useSelector, useDispatch } from 'react-redux'
import { IUserReferral } from '@sparling/financial-common'
import { get } from 'lodash'
import { getReferredUsersData, referredUsersIsLoading } from '../../../store/selectors'
import { getIntroducerUsersRequest } from '../../../store/actions'
import { icons } from '../../../constants/icons'
import { IntroducerUnassignModal } from './IntroducerUnassignModal'

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'relative',
    width: '100%',
    margin: 0,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '& thead tr': {
      height: '60px',
    },
    '& th': {
      color: theme.palette.primary.light,
      opacity: '0.6',
      fontWeight: 400,
      borderColor: 'rgba(233,236,239,0.38)',
    },
    '& tr': {
      height: '80px',
      '&:nth-child(even)': {
        backgroundColor: 'rgba(216,216,216,0.05)',
      },
    },
    '& td': {
      color: theme.palette.primary.light,
      fontSize: 16,
      border: 'none',
      '&.grey': {
        color: '#A5A5A5',
      },
      '&.green': {
        color: 'green',
      },
      '&.red': {
        color: 'red',
      },
      '&.orange': {
        color: 'orange',
      },
      '&.blue': {
        color: 'blue',
      },
      '& .MuiCheckbox-root': {
        pointerEvents: 'none',
      },
    },
  },
  container: {
    maxHeight: '1000px',
  },
  pagination: {
    marginTop: '24px',
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'center',
    '& .MuiPaginationItem-root': {
      color: '#A9AAAD',
      fontSize: '0.75rem',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      fontWeight: 400,
      lineHeight: 1.66,
      letterSpacing: '0.03333em',
    },
    '& .MuiPaginationItem-page.Mui-selected': {
      color: '#51e5a5',
      textAlign: 'center',
      backgroundColor: '#2d3f3d',
      border: '1px solid #51e5a5',
      borderRadius: '4px',
    },
    '& .MuiPagination-ul': {
      flexWrap: 'unset',
    },
  },
  tableTh: {
    textTransform: 'uppercase',
  },
  notFound: {
    backgroundColor: 'transparent !important',
    '& td': {
      paddingTop: theme.spacing(6),
      paddingBottom: theme.spacing(6),
    },
  },
  icon: {
    color: '#a8a9ac',
    '&:hover': {
      color: theme.palette.primary.light,
    },
  },
  anim: {
    color: '#6BFFBF',
    animationDuration: '550ms',
  },
  circle: {
    strokeLinecap: 'round',
  },
}))

const columns: { id: 'name' | 'status' | 'actions'; label: string }[] = [
  { id: 'name', label: 'Name' },
  { id: 'status', label: 'Status' },
  { id: 'actions', label: 'Actions' },
]

interface IntroducerUsersTableProps {
  idSf: string | undefined
  introducerId: number | undefined
  reassignHandler: (value: string) => void
  removeHandler: (value: number) => void
}

export const IntroducerUsersTable: FC<IntroducerUsersTableProps> = ({
  introducerId,
  reassignHandler,
  removeHandler,
  idSf,
}) => {
  const [introducerUnassignModalOpen, setIntroducerUnassignModalOpen] = useState(false)
  const [unassignId, setUnassignId] = useState<string | null | undefined>(null)
  const classes = useStyles()
  const dispatch = useDispatch()

  const isLoadingReferredUsers = useSelector(referredUsersIsLoading)
  const { items, meta } = useSelector(getReferredUsersData)

  const loadData = useCallback(
    (_: ChangeEvent<unknown>, page: number) => {
      if (!introducerId) return
      dispatch(getIntroducerUsersRequest({ introducerId, page, count: 10 }))
    },
    [dispatch, introducerId],
  )

  useEffect(() => {
    if (introducerId) dispatch(getIntroducerUsersRequest({ introducerId, page: 1, count: 10 }))
  }, [dispatch, introducerId])

  const introducerUnassignModalOpenHandle = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setUnassignId(event.currentTarget.dataset.id)
    setIntroducerUnassignModalOpen(true)
  }, [])

  const introducerUnassignModalCloseHandle = useCallback(() => {
    setUnassignId(null)
    setIntroducerUnassignModalOpen(false)
  }, [])

  const reassignIntroducerHandler = useCallback(
    (event) => {
      const { id } = event.currentTarget.dataset
      if (id) reassignHandler(id)
    },
    [reassignHandler],
  )

  const removeIntroducerHandler = useCallback(() => {
    if (unassignId) removeHandler(Number(unassignId))
    introducerUnassignModalCloseHandle()
  }, [introducerUnassignModalCloseHandle, removeHandler, unassignId])

  const renderCell = useCallback(
    (content: IUserReferral, column: 'name' | 'status' | 'actions') => {
      switch (column) {
        case 'name':
          return (
            <Box>
              {get(content, 'referredUser.firstName')} {get(content, 'referredUser.lastName')}
            </Box>
          )
        case 'status':
          return <Box>{get(content, 'referredUser.sumsub.kycStatus', '-')}</Box>
        case 'actions':
          return (
            <Box display="flex" gap={1}>
              <IconButton
                className={classes.icon}
                onClick={reassignIntroducerHandler}
                data-id={content.referredUser.id}
              >
                <span className="material-icons">{icons.DIVERSITY}</span>
              </IconButton>
              <IconButton className={classes.icon} onClick={introducerUnassignModalOpenHandle} data-id={content.id}>
                <span className="material-icons">{icons.PERSON_REMOVE}</span>
              </IconButton>
            </Box>
          )
        default:
          return null
      }
    },
    [classes.icon, introducerUnassignModalOpenHandle, reassignIntroducerHandler],
  )

  return (
    <Paper className={classes.root}>
      {isLoadingReferredUsers ? (
        <CircularProgress
          // variant="indeterminate"
          disableShrink
          className={classes.anim}
          classes={{
            circle: classes.circle,
          }}
          size={40}
          thickness={4}
        />
      ) : (
        <>
          <TableContainer>
            <Table aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} align="left" className={classes.tableTh}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items && items.length > 0 ? (
                  items.map((row) => (
                    <TableRow hover role="checkbox" key={row.id}>
                      {columns.map((column, index: number) => (
                        <TableCell key={index}>{renderCell(row, column.id)}</TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow className={classes.notFound}>
                    <TableCell colSpan={columns.length} align={'center'}>
                      {!isLoadingReferredUsers && 'No results found'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {meta && meta.totalPages > 1 && (
            <Pagination
              className={classes.pagination}
              count={meta.totalPages}
              defaultPage={meta.currentPage}
              shape="rounded"
              onChange={loadData}
              page={meta.currentPage}
            />
          )}
        </>
      )}
      <IntroducerUnassignModal
        idSf={idSf}
        isOpen={introducerUnassignModalOpen}
        modalClose={introducerUnassignModalCloseHandle}
        onSubmit={removeIntroducerHandler}
      />
    </Paper>
  )
}
